import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {Dialog} from '../Dialog'
import {InvitationCodeRecord, InvitationCodeTraveler} from '../../domain/entities/InvitationCode.ts'
import {userReadableDescription} from '../../utils/UserReadableDescription.ts'
import IconClose from '../../assets/icons/close.svg?react'
import IconEdit from '../../assets/icons/edit.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'
import IconPlus from '../../assets/icons/plus.svg?react'
import IconGroup from '../../assets/icons/grupo.svg?react'
import IconIdea from '../../assets/icons/idea.svg?react'

interface SharedAlbumTravelersDialogProps {
    // A shared-album code ("el mismo TripBook impreso varias veces"): one code,
    // `copies` printed copies, one traveler per copy.
    record: InvitationCodeRecord | null
    isSaving: boolean
    onSave: (travelers: InvitationCodeTraveler[]) => Promise<unknown>
}

type Editing = number | 'new' | null

// "Lista de viajeros" — every add/edit/delete is saved straight away, as in the
// "(Vista agencia) Codigo unico" design; there's no global Save button.
export const SharedAlbumTravelersDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, SharedAlbumTravelersDialogProps>(
    ({record, isSaving, onSave}, ref) => {
        const {t} = useTranslation('dashboard')
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
        const [editing, setEditing] = useState<Editing>(null)
        const [name, setName] = useState('')
        const [contact, setContact] = useState('')

        useImperativeHandle(ref, () => ({
            open: () => {
                setEditing(null)
                return dialog.current!.open()
            },
            close: dialog.current!.close,
        }), [])

        const travelers = record?.travelers ?? []
        const copies = record?.copies ?? 1
        const isFull = travelers.length >= copies
        const freeSeats = copies - travelers.length

        const startEdit = (target: Editing) => {
            const current = typeof target === 'number' ? travelers[target] : undefined
            setName(current ? fullName(current) : '')
            setContact(current ? (current.email || current.phone || '') : '')
            setEditing(target)
        }

        const save = () => {
            const trimmedName = name.trim()
            if (!trimmedName || editing === null || isSaving) return
            if (editing === 'new' && isFull) return
            const next = travelers.slice()
            const traveler = toTraveler(trimmedName, contact.trim())
            if (editing === 'new') next.push(traveler)
            else next[editing] = traveler
            persist(next)
        }

        const remove = (index: number) => {
            if (isSaving) return
            persist(travelers.filter((_, i) => i !== index))
        }

        // The update answers with the saved code. A backend that predates
        // `travelers` drops the field without failing, so a non-empty list that
        // comes back missing means it was never stored — say so instead of
        // closing the form as if it had worked.
        const persist = (next: InvitationCodeTraveler[]) => {
            onSave(next).then((saved) => {
                const stored = (saved as InvitationCodeRecord | undefined)?.travelers
                if (next.length > 0 && !stored?.length) {
                    enqueueSnackbar(t('packs.travelerListNotSaved'), {variant: 'error'})
                    return
                }
                setEditing(null)
            }).catch(userReadableDescription)
        }

        const form = (
            <div className="flex flex-col gap-2 p-3 my-2 border border-neutralBlack-100 rounded-xl bg-neutralBlack-50">
                <input autoFocus type="text" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t('packs.travelerListNamePlaceholder')} aria-label={t('packs.travelerListNamePlaceholder')}
                       className="h-[38px] px-3 rounded-[10px] border border-neutralBlack-100 bg-white text-[13.5px] text-neutralBlack-900 outline-none shadow-[0_1px_2px_rgba(0,0,0,.04)] focus:border-neutralBlack-900"/>
                <input type="text" value={contact} onChange={(e) => setContact(e.target.value)}
                       onKeyDown={(e) => {if (e.key === 'Enter') save()}}
                       placeholder={t('packs.travelerListContactPlaceholder')} aria-label={t('packs.travelerListContactPlaceholder')}
                       className="h-[38px] px-3 rounded-[10px] border border-neutralBlack-100 bg-white text-[13.5px] text-neutralBlack-900 outline-none shadow-[0_1px_2px_rgba(0,0,0,.04)] focus:border-neutralBlack-900"/>
                <span className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditing(null)}
                            className="h-8 px-3.5 rounded-full border border-neutralBlack-100 bg-white text-[13px] text-neutralBlack-700 hover:bg-neutralBlack-50">
                        {t('cancel')}
                    </button>
                    <button type="button" onClick={save} disabled={!name.trim() || isSaving}
                            className="h-8 px-4 rounded-full bg-neutralBlack-900 text-[13px] font-medium text-white hover:bg-neutralBlack-700 disabled:opacity-40 disabled:cursor-not-allowed">
                        {t('packs.travelerListSave')}
                    </button>
                </span>
            </div>
        )

        return (
            <Dialog ref={dialog} customWidth="460px" childrenPadding="p-0" panelClassName="rounded-[20px]">
                <div className="flex flex-col max-h-[82vh]">
                    <div className="flex items-start gap-3.5 px-6 pt-[22px] pb-4 border-b border-neutralBlack-100">
                        <span className="flex-1 min-w-0 flex flex-col gap-[3px]">
                            <span className="text-[17px] font-semibold tracking-[-0.01em] text-neutralBlack-900">{t('packs.travelerListTitle')}</span>
                            {record && (
                                <span className="text-[12.5px] text-neutralBlack-500">
                                    {[record.code, t('packs.copiesCount', {count: copies}), record.itinerary?.name].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </span>
                        <button type="button" onClick={() => dialog.current?.close()} aria-label={t('cancel')}
                                className="w-[30px] h-[30px] flex-none flex items-center justify-center rounded-full bg-neutralBlack-100 text-neutralBlack-700 hover:text-neutralBlack-900">
                            <IconClose className="w-[11px] h-[11px]"/>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pt-1.5 pb-2 flex flex-col">
                        {travelers.length === 0 && editing !== 'new' && (
                            <span className="flex flex-col items-center text-center gap-2.5 px-3 pt-[34px] pb-[26px]">
                                <span className="w-14 h-14 rounded-full bg-[#F5EAF7] flex items-center justify-center text-[#6E2F82]">
                                    <IconGroup className="w-7 h-7"/>
                                </span>
                                <span className="text-[15px] font-semibold text-neutralBlack-900">{t('packs.travelerListEmptyTitle')}</span>
                                <span className="max-w-[300px] text-[13px] leading-[1.5] text-neutralBlack-500 text-pretty">{t('packs.travelerListEmptyHelp')}</span>
                            </span>
                        )}
                        {travelers.map((traveler, i) => editing === i ? (
                            <React.Fragment key={i}>{form}</React.Fragment>
                        ) : (
                            <span key={i} className="flex items-center gap-3 py-3 border-b border-neutralBlack-100">
                                <span className="w-[34px] h-[34px] flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-xs font-semibold text-neutralBlack-700">
                                    {initials(fullName(traveler))}
                                </span>
                                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                                    <span className="text-sm text-neutralBlack-900 truncate">{fullName(traveler)}</span>
                                    <span className="text-xs text-neutralBlack-500 truncate">{traveler.email || traveler.phone || '—'}</span>
                                </span>
                                <button type="button" title={t('edit')} aria-label={t('edit')} disabled={isSaving} onClick={() => startEdit(i)}
                                        className="w-[30px] h-[30px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900 disabled:opacity-40">
                                    <IconEdit className="w-[13px] h-[13px]"/>
                                </button>
                                <button type="button" title={t('remove')} aria-label={t('remove')} disabled={isSaving} onClick={() => remove(i)}
                                        className="w-[30px] h-[30px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-[#FBEAE8] hover:text-[#B42318] disabled:opacity-40">
                                    <IconTrash className="w-[13px] h-[13px]"/>
                                </button>
                            </span>
                        ))}
                        {editing === 'new' && form}
                    </div>

                    {isFull && editing !== 'new' && (
                        <div className="mx-6 mb-3 flex items-center gap-2.5 rounded-xl bg-neutralBlack-100 px-3.5 py-[11px]">
                            <IconIdea className="w-4 h-4 flex-none text-neutralBlack-700"/>
                            <span className="flex-1 min-w-0 text-[12.5px] leading-[1.5] text-neutralBlack-700 text-pretty">{t('packs.travelerListFullHelp')}</span>
                        </div>
                    )}

                    <div className="flex items-center gap-2.5 px-6 pt-3.5 pb-[18px] border-t border-neutralBlack-100">
                        <span className="flex-1 flex flex-col gap-0.5">
                            <span className="text-[13px] font-semibold text-neutralBlack-900">
                                {t('packs.travelerListCounter', {count: travelers.length, total: copies})}
                            </span>
                            <span className={`text-[12.5px] ${isFull ? 'text-neutralBlack-500' : 'text-[#0F7A45]'}`}>
                                {isFull ? t('packs.travelerListGroupFull') : t('packs.travelerListFreeSeats', {count: freeSeats})}
                            </span>
                        </span>
                        {editing === null && !isFull && (
                            <button type="button" onClick={() => startEdit('new')}
                                    className="inline-flex items-center gap-[7px] h-[38px] px-4 rounded-full bg-neutralBlack-900 text-[13.5px] font-medium text-white hover:bg-neutralBlack-700">
                                <IconPlus className="w-3 h-3"/>
                                {t('packs.addTraveler')}
                            </button>
                        )}
                    </div>
                </div>
            </Dialog>
        )
    }
)

function fullName(traveler: InvitationCodeTraveler): string {
    return [traveler.firstName, traveler.lastName].filter(Boolean).join(' ')
}

function initials(name: string): string {
    return name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
}

// The design asks for one "Teléfono o email" field; route it to whichever
// traveler field it actually is.
function toTraveler(name: string, contact: string): InvitationCodeTraveler {
    const [firstName, ...rest] = name.split(/\s+/)
    const isEmail = contact.includes('@')
    return {
        firstName,
        lastName: rest.join(' ') || undefined,
        email: isEmail ? contact : undefined,
        phone: !isEmail && contact ? contact : undefined,
    }
}
